<script setup lang="ts">
/**
 * Page « Mon matériel » (V2) — self-service en LECTURE SEULE.
 *
 * Un collaborateur consulte SON propre matériel sans aucune action de mutation.
 * Accessible à tout utilisateur authentifié (pas de permission MATERIAL requise).
 *
 * ⚠️ Page AUTONOME : elle n'utilise PAS `useOnboardingStore` (qui appelle des
 * endpoints exigeant MATERIAL_READ). Elle interroge directement l'endpoint
 * self-service `/material/me` via `fetchMyInventory()`. Aucun bouton de mutation,
 * aucune valeur sensible affichée. Ton visuel aligné sur CollaboratorDetailPage.vue.
 */
import { computed, onMounted, ref } from 'vue'
import {
  ArrowPathIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  InboxArrowDownIcon,
  LockClosedIcon,
  UserIcon,
} from '@heroicons/vue/24/outline'
import {
  fetchMyInventory,
  type ApiAsset,
  type ApiCollaboratorDetail,
  type ApiReplacementTicket,
} from '@/api/materialApi'
import {
  CATEGORY_META,
  attributeLabel,
  isReplacementActive,
  isSensitiveAttribute,
} from '@/v2/onboarding/catalog'
import type { AssetCategory } from '@/v2/onboarding/types'
import OnbAssetStatusBadge from '@/v2/components/onboarding/OnbAssetStatusBadge.vue'
import OnbReplacementStatusBadge from '@/v2/components/onboarding/OnbReplacementStatusBadge.vue'

// ── Chargement (self-service, lecture seule) ───────────────────────────────────
const loading = ref(true)
const error = ref('')
const data = ref<ApiCollaboratorDetail | null>(null)

onMounted(async () => {
  try {
    data.value = await fetchMyInventory()
  } catch {
    error.value = "Impossible de charger votre matériel pour le moment. Réessayez plus tard."
  } finally {
    loading.value = false
  }
})

// ── Identité ───────────────────────────────────────────────────────────────────
const fullName = computed(() => {
  const c = data.value
  if (!c) return ''
  return `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || (c.email ?? 'Mon profil')
})

const identitySubtitle = computed(() => {
  const c = data.value
  if (!c) return ''
  return [c.jobTitle, c.entity].filter(Boolean).join(' · ')
})

// ── Matériel détenu, regroupé par catégorie ────────────────────────────────────
const CATEGORY_ORDER: AssetCategory[] = ['IT', 'TELECOM', 'VEHICLE', 'ACCESS', 'OTHER']

const ownedGroups = computed(() => {
  const owned = data.value?.ownedAssets ?? []
  return CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_META[category].label,
    assets: owned.filter((a) => a.category === category),
  })).filter((g) => g.assets.length > 0)
})

const hasOwned = computed(() => (data.value?.ownedAssets.length ?? 0) > 0)
const hasPending = computed(() => (data.value?.pendingAssets.length ?? 0) > 0)

// ── Remplacements en cours ─────────────────────────────────────────────────────
const activeTickets = computed<ApiReplacementTicket[]>(
  () => data.value?.tickets.filter((t) => isReplacementActive(t.status)) ?? [],
)

// ── Aucun matériel du tout ─────────────────────────────────────────────────────
const isEmpty = computed(
  () => !hasOwned.value && !hasPending.value && activeTickets.value.length === 0,
)

/** Clés tabulaires (numéros de série / IMEI / téléphone) → `tabular-nums`. */
const TABULAR_KEYS = new Set(['serialNumber', 'imei', 'identNumber', 'phoneNumber', 'sdaNumber'])

/** Attributs NON sensibles d'un objet (les valeurs sensibles ne sont jamais affichées). */
function visibleAttributes(asset: ApiAsset): { key: string; value: string }[] {
  return Object.entries(asset.attributes)
    .filter(([key, value]) => value && !isSensitiveAttribute(key))
    .map(([key, value]) => ({ key, value }))
}

/** Y a-t-il au moins un attribut sensible masqué sur cet objet ? */
function hasSensitive(asset: ApiAsset): boolean {
  return Object.entries(asset.attributes).some(([key, value]) => value && isSensitiveAttribute(key))
}
</script>

<template>
  <div class="mx-auto w-full max-w-5xl p-4 md:p-8">
    <!-- ── En-tête ───────────────────────────────────────────────────────────── -->
    <header>
      <h1 class="flex items-center gap-2 text-2xl font-bold">
        <CubeIcon class="h-7 w-7 text-primary" aria-hidden="true" />
        Mon matériel
      </h1>
      <p class="mt-1 text-base text-base-content/80">
        Consultez le matériel qui vous est attribué. Cette page est en lecture seule.
      </p>
    </header>

    <!-- ── État : chargement ─────────────────────────────────────────────────── -->
    <div v-if="loading" class="mt-8 flex items-center justify-center py-16" aria-busy="true">
      <span class="loading loading-spinner loading-lg text-primary" aria-hidden="true" />
      <span class="sr-only">Chargement de votre matériel…</span>
    </div>

    <!-- ── État : erreur ─────────────────────────────────────────────────────── -->
    <div v-else-if="error" role="alert" class="alert alert-error mt-8">
      <ExclamationTriangleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
      <span>{{ error }}</span>
    </div>

    <template v-else-if="data">
      <!-- ── Sous-carte identité ─────────────────────────────────────────────── -->
      <section
        class="card mt-6 border border-base-300 bg-base-100 shadow-sm"
        aria-labelledby="my-identity-title"
      >
        <div class="card-body flex-row items-center gap-3 p-4 md:p-6">
          <span
            class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10"
            aria-hidden="true"
          >
            <UserIcon class="h-6 w-6 text-primary" />
          </span>
          <div class="min-w-0">
            <h2 id="my-identity-title" class="truncate text-lg font-semibold">{{ fullName }}</h2>
            <p v-if="identitySubtitle" class="truncate text-sm text-base-content/80">
              {{ identitySubtitle }}
            </p>
          </div>
        </div>
      </section>

      <!-- ── État : aucun matériel ───────────────────────────────────────────── -->
      <section
        v-if="isEmpty"
        class="card mt-6 border border-base-300 bg-base-100 shadow-sm"
        aria-labelledby="my-empty-title"
      >
        <div class="card-body items-center gap-3 py-12 text-center">
          <CubeIcon class="h-12 w-12 text-base-content/50" aria-hidden="true" />
          <h2 id="my-empty-title" class="text-lg font-semibold">Rien à afficher</h2>
          <p class="max-w-prose text-base text-base-content/80">
            Aucun matériel ne vous est attribué pour le moment.
          </p>
        </div>
      </section>

      <template v-else>
        <!-- ── Remplacements en cours ────────────────────────────────────────── -->
        <section v-if="activeTickets.length" class="mt-6" aria-labelledby="my-replacements-title">
          <h2 id="my-replacements-title" class="flex items-center gap-2 text-lg font-semibold">
            <ArrowPathIcon class="h-5 w-5 text-primary" aria-hidden="true" />
            Remplacements en cours
            <span class="badge badge-sm badge-warning tabular-nums">{{ activeTickets.length }}</span>
          </h2>
          <ul role="list" class="mt-3 grid grid-cols-1 gap-3">
            <li
              v-for="t in activeTickets"
              :key="t.id"
              class="card border border-base-300 bg-base-100 shadow-sm"
            >
              <div class="card-body gap-2 p-4">
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <span class="font-semibold tabular-nums break-all">{{ t.reference }}</span>
                  <OnbReplacementStatusBadge :status="t.status" size="md" />
                </div>
                <p v-if="t.reason" class="text-sm text-base-content/80">Motif : {{ t.reason }}</p>
              </div>
            </li>
          </ul>
        </section>

        <!-- ── Matériel détenu ───────────────────────────────────────────────── -->
        <section v-if="hasOwned" class="mt-6" aria-labelledby="my-owned-title">
          <h2 id="my-owned-title" class="flex items-center gap-2 text-lg font-semibold">
            <CubeIcon class="h-5 w-5 text-primary" aria-hidden="true" />
            Matériel détenu
          </h2>

          <div v-for="group in ownedGroups" :key="group.category" class="mt-5">
            <h3 class="mb-3 flex items-center gap-2 font-semibold text-base-content/80">
              {{ group.label }}
              <span class="badge badge-sm badge-ghost tabular-nums">{{ group.assets.length }}</span>
            </h3>
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <article
                v-for="asset in group.assets"
                :key="asset.id"
                class="card border border-base-300 bg-base-100 shadow-sm"
              >
                <div class="card-body gap-3 p-4">
                  <!-- Titre + statut -->
                  <div class="flex flex-wrap items-start justify-between gap-2">
                    <h4 class="text-base font-semibold break-words">{{ asset.label }}</h4>
                    <OnbAssetStatusBadge :status="asset.status" size="md" />
                  </div>

                  <!-- Attributs non sensibles -->
                  <dl
                    v-if="visibleAttributes(asset).length"
                    class="grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-2"
                  >
                    <div
                      v-for="attr in visibleAttributes(asset)"
                      :key="attr.key"
                      class="flex flex-wrap items-baseline gap-x-2"
                    >
                      <dt class="text-base-content/70">{{ attributeLabel(attr.key) }} :</dt>
                      <dd
                        class="break-words text-base-content"
                        :class="{ 'tabular-nums': TABULAR_KEYS.has(attr.key) }"
                      >
                        {{ attr.value }}
                      </dd>
                    </div>
                  </dl>

                  <!-- Mention discrète : données sensibles masquées (valeur non affichée) -->
                  <p
                    v-if="hasSensitive(asset)"
                    class="inline-flex items-center gap-1.5 text-sm text-base-content/70"
                  >
                    <LockClosedIcon class="h-4 w-4" aria-hidden="true" />
                    Certaines données sensibles ne sont pas affichées ici.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <!-- ── En cours de réception ─────────────────────────────────────────── -->
        <section v-if="hasPending" class="mt-6" aria-labelledby="my-pending-title">
          <h2 id="my-pending-title" class="flex items-center gap-2 text-lg font-semibold">
            <InboxArrowDownIcon class="h-5 w-5 text-primary" aria-hidden="true" />
            En cours de réception
          </h2>
          <div class="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <article
              v-for="asset in data.pendingAssets"
              :key="asset.id"
              class="card border border-base-300 bg-base-100 shadow-sm"
            >
              <div class="card-body gap-3 p-4">
                <div class="flex flex-wrap items-start justify-between gap-2">
                  <h4 class="text-base font-semibold break-words">{{ asset.label }}</h4>
                  <OnbAssetStatusBadge :status="asset.status" size="md" />
                </div>
                <dl
                  v-if="visibleAttributes(asset).length"
                  class="grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-2"
                >
                  <div
                    v-for="attr in visibleAttributes(asset)"
                    :key="attr.key"
                    class="flex flex-wrap items-baseline gap-x-2"
                  >
                    <dt class="text-base-content/70">{{ attributeLabel(attr.key) }} :</dt>
                    <dd
                      class="break-words text-base-content"
                      :class="{ 'tabular-nums': TABULAR_KEYS.has(attr.key) }"
                    >
                      {{ attr.value }}
                    </dd>
                  </div>
                </dl>
              </div>
            </article>
          </div>
        </section>
      </template>

      <!-- ── Note discrète : signature à venir ───────────────────────────────── -->
      <p class="mt-8 text-sm text-base-content/70">
        La signature des fiches de remise sera disponible prochainement.
      </p>
    </template>
  </div>
</template>
